import { useCallback, useMemo, useState } from 'react'

/**
 * Explicit reorder mode for a table: a draft order the operator edits by dragging, plus the id
 * sequence the backend expects.
 *
 * The hook owns no transport. It reports `ids`; the page decides which endpoint they go to.
 *
 * Leaving the mode always drops the draft and falls back to `rows`. That covers both exits with
 * one path: on cancel `rows` still holds the pre-edit order, and after a save the page reloads
 * first, so by the time it exits `rows` already holds the order the server confirmed. Keeping a
 * separate "committed" copy would only add a way for the two to disagree.
 */
export interface RowReorder<T> {
  /** Draft order while the mode is open, the source rows otherwise. */
  rows: T[]
  active: boolean
  open: () => void
  /** Leave the mode. Reload the source rows BEFORE calling this after a successful save. */
  exit: () => void
  move: (fromId: number, toId: number) => void
  /** Row ids in the order currently on screen — the reorder payload. */
  ids: number[]
  /** The draft differs from the source order, so there is something to save. */
  dirty: boolean
}

export function useRowReorder<T>(rows: T[], getId: (row: T) => number): RowReorder<T> {
  // null means "not in reorder mode" — the hook then mirrors `rows` verbatim, so a reload of the
  // page data is picked up without any syncing effect.
  const [draft, setDraft] = useState<T[] | null>(null)

  const current = draft ?? rows

  const open = useCallback(() => setDraft(rows), [rows])

  const exit = useCallback(() => setDraft(null), [])

  const move = useCallback(
    (fromId: number, toId: number) => {
      if (fromId === toId) return

      setDraft((prev) => {
        if (prev === null) return prev

        const from = prev.findIndex((row) => getId(row) === fromId)
        const to = prev.findIndex((row) => getId(row) === toId)
        if (from === -1 || to === -1) return prev

        const next = [...prev]
        const [moved] = next.splice(from, 1)
        next.splice(to, 0, moved)

        return next
      })
    },
    [getId]
  )

  const ids = useMemo(() => current.map(getId), [current, getId])

  const dirty = useMemo(() => {
    if (draft === null) return false
    if (draft.length !== rows.length) return true

    return draft.some((row, index) => getId(row) !== getId(rows[index]))
  }, [draft, rows, getId])

  return { rows: current, active: draft !== null, open, exit, move, ids, dirty }
}
